import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Grid, Box, LinearProgress, ButtonGroup, Collapse, CircularProgress } from '@mui/material'
import Zoom from '@mui/material/Zoom'
import TextField from '@mui/material/TextField'
import Typography from '@app/stories/Typography'
import { useCurrentUser, useCurrentClinic } from '@app/context/CurrentUser'
import Button from '@app/stories/Button'
import { isEmail, isPhone } from '@app/libs/validators'
import SelectField from '@app/stories/Select'
import { createAccount } from '@app/firebase/functions'
import OrderDetailsCard from '@app/components/OrderDetailsCard'
import Icon from '@app/stories/Icons'
import { formatDateForDatePickerUI } from '@app/libs/formatters'

import useEmrOrders from '@app/hooks/useEmrOrders'

import StepperContainer from '@app/containers/StepperContainer'
import OrdersPatientInfoForm from '@app/components/EmrOrderFormComponents/OrdersPatientInfoForm'
import DiagnosisCodeSearch from '@app/components/EmrOrderFormComponents/DiagnosisSearch'
import ServiceSearch from '@app/components/EmrOrderFormComponents/ServiceSearch'
import MedicationSearch from '@app/components/EmrOrderFormComponents/MedicationSearch'
import OrderNotes from '@app/components/EmrOrderFormComponents/OrderNotes'
import ReviewOrder from '@app/components/EmrOrderFormComponents/ReviewOrder'
import ReviewMedicationOrder from '@app/components/EmrOrderFormComponents/ReviewMedicationOrder'

import FilterContainer from '@app/components/EmrOrderFilters/FilterContainer'
import TypeFilters from '@app/components/EmrOrderFilters/TypeFilters'
import DateFilters from '@app/components/EmrOrderFilters/DateFilters'
import LastnameFilter from '@app/components/EmrOrderFilters/LastnameFilter'

const determineLookupType = (lookUpValue) => {
  if (!lookUpValue) return 'empty'

  if (isEmail(lookUpValue)) return 'email'
  if (isPhone(lookUpValue)) return 'phone'

  return 'memberId'
}

const ProviderOrders = () => {
  const { profileData, authData } = useCurrentUser()

  const { npi: mainProviderNPI, plansAssociated: clinicPlansAssociated = {} } = useCurrentClinic()

  const {
    createNewOrder,
    diagnosisCodeData,
    setDiagnosisCodeData,
    serviceSelected,
    setServiceSelected,
    medicationSelected,
    setMedicationSelected,
    SIG,
    setSIG,
    quantity,
    setQuantity,
    drugAllergies,
    setDrugAllergies,
    otherMedications,
    setOtherMedications,
    medicalConditions,
    setMedicalConditions,
    pregnant,
    setPregnant,
    nursing,
    setNursing,
    safetyCap,
    setSafetyCap,
    providerFirstname,
    setProviderFirstname,
    providerNPI,
    setProviderNPI,
    noteContent,
    setNoteContent,
    currentFile,
    setCurrentFile,
    setPatientUserID,
    firstname,
    setFirstname,
    lastname,
    setLastname,
    email,
    setEmail,
    phone,
    setFormattedPhone,
    dob,
    setDob,
    gender,
    setGender,
    street,
    setStreet,
    street2,
    setStreet2,
    city,
    setCity,
    state,
    setState,
    zip,
    setZip,
    submitDisabled,
    clearPatientInfo,
    clearGeneralOrderInfo,
    clearAllOrderInfo,
    fetchEmrOrders,
    displayEmrOrders,
    emrOrdersLoading,
    ordersLimit,
    setOrdersLimit,
    setOrdersFilter,
    ordersFilter,
    setPatientPlans,
    patientPlans,
    setReferenceVoucher,
  } = useEmrOrders({
    provider: {
      id: profileData.id,
      firstname: profileData.firstname,
      lastname: profileData.lastname,
      email: profileData.email,
      phone: profileData.phone,
      npi: mainProviderNPI,
      clinicID: authData.organizationId,
      clinicName: authData.organizationName,
    },
  })

  const [newOrderOpen, setNewOrderOpen] = useState(false)

  const [orderType, setOrderType] = useState('')

  const referralOrderBubbleInfo = [
    { stepName: 'Patient Info' },
    { stepName: 'Diagnosis Code Selection' },
    { stepName: 'Service Selection' },
    { stepName: 'Additional Notes' },
    { stepName: 'Review Order' },
  ]

  const medicationOrderBubbleInfo = [
    { stepName: 'Patient Info' },
    { stepName: 'Diagnosis Code Selection' },
    { stepName: 'Medication Selection' },
    { stepName: 'Additional Notes' },
    { stepName: 'Review Order' },
  ]

  // progress bar
  const progressInterval = useRef(null)
  const [progress, setProgress] = useState(0)
  const [buffer, setBuffer] = useState(10)

  const [lookupInputValue, setLookupInputValue] = useState('')

  const [patientProfile, setPatientProfile] = useState(null)

  const [nextDisabled, setNextDisabled] = useState(false)

  const [errorMessage, setErrorMessage] = useState(null)

  const [algoliaFilters, setAlgoliaFilters] = useState(null)

  const [ordersTab, setOrdersTab] = useState('new')

  const [openFilters, setOpenFilters] = useState(false)

  const [selectedPlan, setSelectedPlan] = useState('default')

  const [newPlan, setNewPlan] = useState('default')
  const [existingPlan, setExistingPlan] = useState('default')

  const [selectedPlanLabel, setSelectedPlanLabel] = useState(null)

  // Add missing state variables for user selection
  const [matchingUsers, setMatchingUsers] = useState([])
  const [showUserSelection, setShowUserSelection] = useState(false)

  const defaultPlanOptions = [
    { value: 'default', label: 'Select a plan' },
    window.location.hostname !== 'mishe.co'
      ? { value: 'qwGk5c8R6wfOvVjh6SJ7', label: 'Mishe Health Plan' }
      : { value: 'UUrfH2FiTx2LjkdzUhnP', label: 'Mishe Health Plan' },
  ]

  const planOptions = useMemo(() => {
    const combinedPlans = {
      ...patientPlans,
      ...clinicPlansAssociated,
    }

    return [
      ...defaultPlanOptions,
      ...Object.keys(combinedPlans).map((planID) => ({
        value: planID,
        label: combinedPlans[planID]?.name || 'Unnamed Plan',
      })),
    ]
  }, [patientPlans, clinicPlansAssociated, defaultPlanOptions])

  useEffect(() => {
    if (!openFilters && ordersTab !== 'new') {
      fetchEmrOrders()
    }
  }, [openFilters, ordersTab])

  const handleFilterClear = () => {
    setOrdersFilter({
      startDate: '',
      endDate: '',
      lastname: '',
      medication: false,
      lab: true,
      referral: true,
      other: true,
      paid: true,
      unpaid: true,
      failed: true,
    })
  }

  const handleFilterReset = () => {
    setOrdersFilter({
      startDate: '',
      endDate: '',
      lastname: '',
      medication: false,
      lab: false,
      referral: false,
      other: false,
      paid: false,
      unpaid: false,
      failed: false,
    })
  }

  const startProgressBar = () => {
    setProgress(0)
    setBuffer(10)
    clearInterval(progressInterval.current)
    progressInterval.current = null

    // startErrorTimer()
    progressInterval.current = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0
        }
        const diff = Math.random() * 10
        return Math.min(oldProgress + diff, 100)
      })
      setBuffer((oldProgress) => {
        if (oldProgress === 100) {
          return 0
        }
        const diff = Math.random() * 10
        return Math.min(oldProgress + diff, 100)
      })
    }, 500)
  }

  const stopProgressBar = () => {
    clearInterval(progressInterval.current)
    progressInterval.current = null
    setProgress(0)
    setBuffer(0)
  }

  const cleanUp = () => {
    setSelectedPlan('default')
    setSelectedPlanLabel(null)
    setNewPlan('default')
    setExistingPlan('default')
    clearAllOrderInfo()
    setOrderType('')
    setNewOrderOpen(false)
    setGender(null)
    setErrorMessage(null)
    setNextDisabled(false)
    setPatientProfile(null)
    setLookupInputValue('')
    setShowUserSelection(false)
    setMatchingUsers([])
  }

  const passPatientToOrder = (patientInfo) => {
    if (!patientInfo) {
      return
    }
    console.log(patientInfo)
    console.log('Passing patient to order')
    console.log(patientInfo)
    setPatientUserID(patientInfo?.id || patientInfo?.uid || '')
    setFirstname(patientInfo?.firstname || '')
    setLastname(patientInfo?.lastname || '')
    setEmail(patientInfo?.email || '')
    setFormattedPhone(patientInfo?.phone || '')
    setDob(formatDateForDatePickerUI(patientInfo?.dob || patientInfo?.birthday) || '')
    setGender(patientInfo?.gender || '')
    setStreet(patientInfo?.address?.street || '')
    setStreet2(patientInfo?.address?.street2 || '')
    setCity(patientInfo?.address?.city || '')
    setState(patientInfo?.address?.state || '')
    setZip(patientInfo?.address?.zip || '')
    setPatientPlans(patientInfo?.plansAssociated || {})
  }

  const handleUserSelection = (userId) => {
    const selectedUser = matchingUsers.find((user) => user.id === userId || user.uid === userId)
    if (selectedUser) {
      setPatientProfile(selectedUser)
      passPatientToOrder(selectedUser)
      setShowUserSelection(false)
      setMatchingUsers([])
      setNextDisabled(true) // Keep Next button disabled after selection
    }
  }

  const handleAccount = useCallback(async () => {
    setNextDisabled(true)
    startProgressBar()

    try {
      // Validate and format input
      const lookupType = determineLookupType(lookupInputValue)
      if (lookupType === 'empty') {
        setErrorMessage('Please enter a valid email or phone number')
        setNextDisabled(false)
        return false
      }

      let formattedLookupValue = lookupInputValue.trim()
      if (lookupType === 'email') {
        formattedLookupValue = formattedLookupValue.toLowerCase()
      }

      // Call the cloud function with validated input
      const response = await createAccount({
        lookupValue: formattedLookupValue,
        lookupType,
        clinicID: authData.organizationId,
      })

      console.log('Response from createAccount:', response)

      // Reset any previous matching users
      setMatchingUsers([])
      setShowUserSelection(false)

      // Check for users array in the response (new format)
      if (response.users && response.users.length > 0) {
        setErrorMessage(null)

        if (response.users.length === 1) {
          // Single user case - auto-select
          setPatientProfile(response.users[0])
          passPatientToOrder(response.users[0])
          return response.users[0]
        } else {
          // Multiple users case - show selection UI
          setMatchingUsers(response.users)
          setShowUserSelection(true)
          setNextDisabled(false) // Re-enable button to allow for another search if needed
          return false
        }
      }
      // Fallback to legacy format with single user
      else if (response.user) {
        setErrorMessage(null)
        setPatientProfile(response.user)
        passPatientToOrder(response.user)
        return response.user
      }

      // Handle error scenarios
      let errorMsg = 'Failed to process request'

      if (response.error?.errorInfo) {
        // Extract error message from Firebase error info
        const errorCode = response.error.errorInfo.code
        switch (errorCode) {
          case 'auth/invalid-email':
            errorMsg = 'Invalid email format'
            break
          case 'auth/invalid-phone-number':
            errorMsg = 'Invalid phone number format'
            break
          case 'auth/email-already-exists':
          case 'auth/phone-number-already-exists':
            // These shouldn't happen anymore with the new API
            errorMsg = 'User account exists but could not be retrieved'
            break
        }
      }

      setErrorMessage(errorMsg)
      setNextDisabled(false) // Re-enable button on error
      return false
    } catch (error) {
      console.error('Error in handleAccount:', error)
      setErrorMessage('An unexpected error occurred')
      setNextDisabled(false) // Re-enable button on error
      return false
    } finally {
      stopProgressBar()
      // Next button state now depends on the logic in the try block
    }
  }, [lookupInputValue, authData.organizationId, passPatientToOrder])

  useEffect(() => {
    if (!selectedPlan || selectedPlan === 'default') {
      setAlgoliaFilters(null)
      return undefined
    }
    setAlgoliaFilters(`networkId:${selectedPlan}`)
  }, [selectedPlan])

  useEffect(() => {
    if (!newOrderOpen) {
      setSelectedPlan('default')
      setSelectedPlanLabel(null)
      setNewPlan('default')
      setExistingPlan('default')
      clearPatientInfo()
      clearGeneralOrderInfo()
      setLookupInputValue('')
    }
  }, [newOrderOpen])

  const handleTabChange = (newValue) => {
    setOrdersTab(newValue)
    if (openFilters) {
      setOpenFilters(false)
    }
  }

  const handlePlanSelection = useCallback(
    (typeOfEnrollment, planID) => {
      setExistingPlan(typeOfEnrollment === 'existing' ? planID : 'default')
      setNewPlan(typeOfEnrollment === 'new' ? planID : 'default')
      setSelectedPlan(planID)
      setSelectedPlanLabel(planOptions.find((option) => option.value === planID)?.label)
    },
    [planOptions],
  )

  const handlePlanReset = () => {
    setSelectedPlan('default')
    setSelectedPlanLabel(null)
    setNewPlan('default')
    setExistingPlan('default')
  }

  const handleFinished = useCallback(() => {
    setReferenceVoucher({ preReg: true })
    createNewOrder(cleanUp)
  }, [createNewOrder, cleanUp, setReferenceVoucher])

  const buildStepperComponents = useCallback(() => {
    if (orderType === 'referral') {
      return (
        <StepperContainer
          bubbleInfo={referralOrderBubbleInfo}
          disableSubmit={submitDisabled}
          stepControlDetails={{
            show: true,
            handleFinished: handleFinished,
          }}
        >
          <OrdersPatientInfoForm
            key='patient-info'
            values={{
              firstname: firstname || '',
              lastname: lastname || '',
              email: email || '',
              phone: phone || '',
              dob: dob || '',
              gender: gender || '',
              street: street || '',
              street2: street2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            }}
            setters={{
              setFirstname,
              setLastname,
              setEmail,
              setPhone: setFormattedPhone,
              setDob,
              setGender,
              setStreet,
              setStreet2,
              setCity,
              setState,
              setZip,
            }}
          />
          <DiagnosisCodeSearch
            key='diagnosis-code'
            values={{
              diagnosisCodeData,
            }}
            setters={{
              setDiagnosisCodeData,
            }}
          />
          <Box>
            {!serviceSelected && (
              <Box>
                <Box display='flex' gap='16px' alignItems='center' mb={2}>
                  <Typography variant='h4' margin='0px'>
                    {selectedPlan !== 'default'
                      ? selectedPlanLabel
                      : 'Select a plan to view services'}
                  </Typography>
                  <Zoom in={selectedPlan !== 'default'}>
                    <Box>
                      <Button
                        variant='outlined'
                        color='primary'
                        size='small'
                        onClick={handlePlanReset}
                      >
                        <Box display='flex' alignItems='center' gap='4px'>
                          <Icon name='edit' />
                          <Typography variant='body1' margin='0px'>
                            Change Plan
                          </Typography>
                        </Box>
                      </Button>
                    </Box>
                  </Zoom>
                </Box>
                <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} gap='16px'>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    <Collapse in={selectedPlan === 'default'} timeout={{ enter: 500, exit: 500 }}>
                      <Typography variant='h5' margin='0px'>
                        Patient Plans
                      </Typography>
                      <SelectField
                        label=''
                        value={existingPlan}
                        margin='none'
                        onChange={(e) => {
                          if (e.target.value === 'default') {
                            return
                          }
                          handlePlanSelection('existing', e.target.value)
                        }}
                        options={
                          patientPlans && Object.keys(patientPlans).length > 0
                            ? (() => {
                                const options = Object.keys(patientPlans).map((planID) => ({
                                  value: planID,
                                  label: patientPlans[planID].name,
                                }))
                                options.unshift({
                                  value: 'default',
                                  label: 'Select a plan',
                                })
                                return options
                              })()
                            : defaultPlanOptions
                        }
                      />
                    </Collapse>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    <Collapse in={selectedPlan === 'default'} timeout={{ enter: 500, exit: 500 }}>
                      <Typography variant='h5' margin='0px'>
                        Enroll in new plan
                      </Typography>
                      <SelectField
                        label=''
                        value={newPlan}
                        margin='none'
                        disabled={
                          !clinicPlansAssociated || Object.keys(clinicPlansAssociated).length === 0
                        }
                        onChange={(e) => {
                          if (e.target.value === 'default') {
                            return
                          }
                          handlePlanSelection('new', e.target.value)
                        }}
                        options={
                          clinicPlansAssociated && Object.keys(clinicPlansAssociated).length > 0
                            ? (() => {
                                const options = Object.keys(clinicPlansAssociated).map(
                                  (planID) => ({
                                    value: planID,
                                    label: clinicPlansAssociated[planID].name,
                                  }),
                                )
                                options.unshift({
                                  value: 'default',
                                  label: 'Select a plan',
                                })
                                return options
                              })()
                            : []
                        }
                      />
                    </Collapse>
                  </Box>
                </Box>
              </Box>
            )}
            <Collapse in={algoliaFilters && true} timeout={{ enter: 500, exit: 500 }}>
              <ServiceSearch
                key='service-selection'
                values={{
                  serviceSelected,
                  algoliaFilters,
                }}
                setters={{
                  setServiceSelected,
                }}
              />
            </Collapse>
          </Box>
          <OrderNotes
            key='additional-notes'
            values={{
              providerFirstname,
              providerNPI,
              noteContent,
              currentFile,
            }}
            setters={{
              setProviderFirstname,
              setProviderNPI,
              setNoteContent,
              setCurrentFile,
            }}
          />
          <ReviewOrder
            key='review-order'
            values={{
              firstname,
              lastname,
              email,
              phone,
              dob,
              gender,
              street,
              street2,
              city,
              state,
              zip,
              noteContent,
              currentFile,
              serviceSelected,
              diagnosisCodeData,
              providerFirstname,
              providerNPI,
            }}
          />
        </StepperContainer>
      )
    } else {
      return (
        <StepperContainer
          bubbleInfo={medicationOrderBubbleInfo}
          disableSubmit={submitDisabled}
          stepControlDetails={{
            show: true,
            handleFinished: handleFinished,
          }}
        >
          <OrdersPatientInfoForm
            key='patient-info'
            values={{
              firstname: firstname || '',
              lastname: lastname || '',
              email: email || '',
              phone: phone || '',
              dob: dob || '',
              gender: gender || '',
              street: street || '',
              street2: street2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            }}
            setters={{
              setFirstname,
              setLastname,
              setEmail,
              setPhone: setFormattedPhone,
              setDob,
              setGender,
              setStreet,
              setStreet2,
              setCity,
              setState,
              setZip,
            }}
          />
          <DiagnosisCodeSearch
            key='diagnosis-code'
            values={{
              diagnosisCodeData,
            }}
            setters={{
              setDiagnosisCodeData,
            }}
          />
          <MedicationSearch
            key='medication-selection'
            values={{
              medicationSelected,
              SIG,
              quantity,
              drugAllergies,
              otherMedications,
              medicalConditions,
              pregnant,
              nursing,
              safetyCap,
            }}
            setters={{
              setMedicationSelected,
              setSIG,
              setQuantity,
              setDrugAllergies,
              setOtherMedications,
              setMedicalConditions,
              setPregnant,
              setNursing,
              setSafetyCap,
            }}
          />
          <OrderNotes
            key='additional-notes'
            values={{
              providerFirstname,
              providerNPI,
              noteContent,
              currentFile,
            }}
            setters={{
              setProviderFirstname,
              setProviderNPI,
              setNoteContent,
              setCurrentFile,
            }}
          />
          <ReviewMedicationOrder
            key='review-order'
            values={{
              firstname,
              lastname,
              email,
              phone,
              dob,
              gender,
              street,
              street2,
              city,
              state,
              zip,
              noteContent,
              currentFile,
              medicationSelected,
              SIG,
              quantity,
              drugAllergies,
              otherMedications,
              medicalConditions,
              pregnant,
              nursing,
              safetyCap,
              diagnosisCodeData,
              providerFirstname,
              providerNPI,
            }}
          />
        </StepperContainer>
      )
    }
  }, [
    orderType,
    firstname,
    lastname,
    email,
    phone,
    dob,
    gender,
    street,
    street2,
    city,
    state,
    zip,
    diagnosisCodeData,
    serviceSelected,
    medicationSelected,
    SIG,
    quantity,
    drugAllergies,
    otherMedications,
    medicalConditions,
    pregnant,
    nursing,
    safetyCap,
    providerFirstname,
    providerNPI,
    noteContent,
    currentFile,
    algoliaFilters,
    submitDisabled,
  ])

  return (
    // if orders exist iterate through them and display them, if there are no orders display a message
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant='h1' gutterBottom>
          Orders
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box
          display='flex'
          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
          sx={{
            width: '100%',
            justifyContent: {
              xs: 'center',
              sm: 'space-between',
            },
          }}
        >
          <Box
            display='flex'
            sx={{
              width: { xs: '100%', sm: '500px' },
              marginBottom: { xs: '16px', md: '0px' },
            }}
          >
            <ButtonGroup style={{ width: '100%' }}>
              <Button
                style={{ minWidth: 'auto', width: '33.4%' }}
                variant={ordersTab === 'new' ? 'contained' : 'outlined'}
                color='primary'
                onClick={() => handleTabChange('new')}
              >
                New Order
              </Button>
              <Button
                style={{ minWidth: 'auto', width: '33.4%' }}
                variant={ordersTab === 'sent' ? 'contained' : 'outlined'}
                color='primary'
                onClick={() => handleTabChange('sent')}
              >
                Sent Orders
              </Button>
              <Button
                style={{ minWidth: 'auto', width: '33.4%' }}
                variant={ordersTab === 'received' ? 'contained' : 'outlined'}
                color='primary'
                onClick={() => handleTabChange('received')}
              >
                Received Orders
              </Button>
            </ButtonGroup>
          </Box>
          {ordersTab !== 'new' && (
            <Box
              display='flex'
              sx={{
                width: { xs: '100%', sm: '500px' },
                marginBottom: { xs: '8px', sm: '0px' },
                justifyContent: { xs: 'center', sm: 'flex-end' },
              }}
              gap='16px'
            >
              <Box
                sx={{
                  width: { xs: '60%', md: 'auto' },
                }}
              >
                <Button
                  style={{ width: '100%' }}
                  variant={openFilters ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (openFilters) {
                      handleFilterClear()
                    } else {
                      handleFilterReset()
                    }
                    setOpenFilters(!openFilters)
                  }}
                >
                  Open Filters
                </Button>
              </Box>
              <Box
                sx={{
                  width: { xs: '40%', md: 'auto' },
                }}
                minWidth='120px'
              >
                <SelectField
                  label='Orders Shown'
                  value={ordersLimit}
                  margin='none'
                  onChange={(e) => setOrdersLimit(e.target.value)}
                  options={[
                    { value: '10', label: '10' },
                    { value: '30', label: '30' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
      <FilterContainer
        open={openFilters}
        leftComponents={
          <TypeFilters
            filterFunction={(type, value) => {
              setOrdersFilter({ ...ordersFilter, [type]: value })
            }}
          />
        }
        rightComponents={
          <>
            <DateFilters
              filterFunction={(type, value) => {
                setOrdersFilter({ ...ordersFilter, [type]: value })
              }}
            />
            <LastnameFilter
              filterFunction={(type, value) => {
                setOrdersFilter({ ...ordersFilter, [type]: value })
              }}
            />
          </>
        }
        actionComponents={
          <>
            <Box padding='8px'>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => {
                  handleFilterClear()
                  setOpenFilters(false)
                }}
              >
                Clear Filters
              </Button>
            </Box>
            <Box padding='8px'>
              <Button
                variant='contained'
                color='primary'
                onClick={() => {
                  fetchEmrOrders()
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </>
        }
      />
      {ordersTab === 'sent' && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {displayEmrOrders?.sent?.length > 0 ? (
              displayEmrOrders?.sent?.map((order) => {
                if (order?.display) {
                  return OrderDetailsCard({
                    order,
                    variant: 'grid',
                  })
                }
                return null
              })
            ) : (
              <>
                {emrOrdersLoading === false && (
                  <Grid item xs={12}>
                    <Typography variant='h4' gutterBottom>
                      You have not placed any orders.
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Grid>
      )}
      {ordersTab === 'received' && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {displayEmrOrders?.received?.length > 0 ? (
              displayEmrOrders?.received?.map((order) => {
                if (order?.display) {
                  return OrderDetailsCard({
                    order,
                    variant: 'grid',
                  })
                }
                return null
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant='h4' gutterBottom>
                  You have not received any orders.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {ordersTab === 'new' && (
        <>
          <Grid item xs={12}>
            <Typography variant='h5' gutterBottom>
              Actions
            </Typography>
            <Box
              display='flex'
              gap='16px'
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Button
                variant={orderType === 'referral' ? 'contained' : 'outlined'}
                color='primary'
                onClick={() => {
                  if (orderType === 'referral') {
                    setOrderType('')
                    setNewOrderOpen(false)
                    return
                  }
                  setOrderType('referral')
                  setNewOrderOpen(true)
                  setPatientProfile(null)
                  setNextDisabled(false)
                }}
              >
                New referral order
              </Button>
              <Button
                variant={orderType === 'medication' ? 'contained' : 'outlined'}
                color='primary'
                onClick={() => {
                  cleanUp()
                  if (orderType === 'medication') {
                    setOrderType('')
                    setNewOrderOpen(false)
                    return
                  }
                  setOrderType('medication')
                  setNewOrderOpen(true)
                  setPatientProfile(null)
                  setNextDisabled(false)
                }}
                style={{
                  margin: '0px',
                }}
              >
                New medication order
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            {newOrderOpen && (
              <Box
                mb={3}
                style={{
                  // light purple
                  backgroundColor: '#f5f3ff',
                  padding: '1rem',
                  borderRadius: '1rem',
                  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Typography variant='h4' color='primary' mb={1}>
                      Creating a new {orderType} order
                    </Typography>
                    <Typography variant='h5' color='primary' mb={0}>
                      Please enter the patient's member ID or email address to continue.
                    </Typography>
                  </Grid>
                  {errorMessage && (
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Typography variant='body1' color='error'>
                        {errorMessage}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12} md={3} lg={3}>
                    <TextField
                      id='patient-lookup'
                      value={lookupInputValue}
                      onChange={(e) => setLookupInputValue(e.target.value)}
                      disabled={nextDisabled || patientProfile !== null}
                      label='Patient Identifier'
                      variant='outlined'
                      fullWidth
                      style={{
                        backgroundColor: '#fff',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={5} lg={5}>
                    <Button
                      variant='contained'
                      color='primary'
                      fullWidth
                      disabled={nextDisabled || patientProfile !== null}
                      onClick={handleAccount}
                    >
                      Next
                    </Button>
                  </Grid>
                  {/* Add user selection dropdown when multiple users are found */}
                  {showUserSelection && matchingUsers.length > 1 && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          backgroundColor: '#ffffff',
                          padding: '16px',
                          borderRadius: '8px',
                          marginTop: '16px',
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant='h5' gutterBottom>
                          Multiple patients found. Please select one:
                        </Typography>
                        <Box
                          display='flex'
                          flexDirection='column'
                          gap='8px'
                          sx={{
                            maxHeight: '600px',
                            overflowY: 'auto',
                          }}
                          padding='8px'
                          borderRadius='8px'
                        >
                          {matchingUsers
                            .filter((user) => {
                              // Filter out users without sufficient identifying information
                              return (
                                user &&
                                ((user.firstname && user.lastname) || user.email || user.phone)
                              )
                            })
                            .map((user) => {
                              // Build a display name that shows available information
                              const namePart =
                                user.firstname || user.lastname
                                  ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                                  : 'Unknown Name'

                              const emailPart = user.email || ''
                              const phonePart = user.phone || ''

                              return (
                                <Box>
                                  <Button
                                    variant='outlined'
                                    onClick={() => handleUserSelection(user.id || user.uid)}
                                    key={`user-${user.id}-plan-${user.planName}`}
                                    sx={{
                                      justifyContent: 'flex-start',
                                      textAlign: 'left',
                                      width: '100%',
                                      paddingLeft: '16px',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                      },
                                    }}
                                  >
                                    <Box
                                      display='flex'
                                      flexDirection='row'
                                      justifyContent='space-between'
                                      alignItems='center'
                                      width='100%'
                                    >
                                      <Box sx={{ fontWeight: 'bold' }}>
                                        {namePart}
                                        {user.planName ? ` (${user.planName})` : ''}
                                      </Box>
                                      <Box display='flex' gap='16px'>
                                        <Typography variant='body2' color='textSecondary'>
                                          {emailPart}
                                        </Typography>
                                        <Typography variant='body2' color='textSecondary'>
                                          {phonePart}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Button>
                                </Box>
                              )
                            })}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {progressInterval.current && (
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <LinearProgress variant='buffer' value={progress} valueBuffer={buffer} />
                    </Grid>
                  )}
                  {patientProfile && (
                    <Box padding='16px 0px 0px 24px' width='100%'>
                      {buildStepperComponents()}
                    </Box>
                  )}
                </Grid>
              </Box>
            )}
          </Grid>
        </>
      )}
      {emrOrdersLoading && (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}
        >
          <CircularProgress />
        </div>
      )}
    </Grid>
  )
}

export default ProviderOrders
